/**
 *  @file       ks-cache.js
 *              Wallet API Keystore cache - in-memory cache of keystores
 *
 *  @author     Wes Garland    - wes@kingsds.network
 *  @author     Ryan Rossiter  - ryan@kingsds.network
 *  @date       August 2019
 */

'use strict';

const { assert } = require('dcp/common/dcp-assert');
const debugging = require('dcp/debugging').scope('wallet/cache');
const { Keystore } = require('./keystore');
const { Address, PrivateKey } = require('./eth');
const KVIN = new (require('kvin').KVIN);
KVIN.userCtors[Address.name] = Address;

var storage = {};

function buildCacheKey(name, contextId) /* future: cacheKey to encode ctor.name */
{
  var cacheKey = name;

  if (!contextId)
    return name;

  return `<${escape(contextId)}::${escape(name)}>`;
}

function isThenable(obj)
{
  if (typeof obj !== 'object' || obj === null)
    return false;
  if (obj instanceof Promise)
    return true;
  if (typeof obj.then === 'function')
    return true;
  return false;
}

/** Clear the entire cache, for all contexts */
exports.reset = function ksCache$$reset()
{
  debugging() && console.debug('wallet - ksCache reset cache');
  storage = {};
}

/**
 * Retrieve an keystore from the cache. Before retrieval, we resolve any
 * promises which were left pending by eg wallet.add().
 */
exports.get = async function ksCache$$get(ctor, name, contextId)
{
  const cacheKey = buildCacheKey(name, contextId);
  var entry = storage.hasOwnProperty(cacheKey) && storage[cacheKey];

  debugging() && console.debug(`wallet - ksCache ${entry ? 'hit' : 'miss'} on ${ctor.name} as ${cacheKey}`);
  debugging() && entry && console.debug(`wallet - ksCache entry is ${ctor.name} as ${cacheKey} => ${entry.ks ? entry.ks.address : entry.ks} (${entry.ks && entry.ks.label})`);

  if (!entry)
    return entry;

  if (isThenable(entry.ks))
  {
    debugging() && console.debug(`wallet - ksCache entry.ks is a Promise - resolving`);

    try
    {
      storage[cacheKey].ks = await entry.ks;
    }
    catch(error)
    {
      /* We can land here in the middle of a wallet::get in some crazy
       * circumstances, like if wallet::add used the URL form and the
       * URL was not to a keystore JSON. That can ONLY happen after
       * the wallet.addId(), though, so the developer should be aware
       * that that could fail.
       *
       * We leave the entry set to null, though, so that we don't
       * accidentally generate a random identity.  The only way
       * for the program to recover from this is to clear the cache
       * and re-addId when the wallet.get() promise rejects.
       */
      storage[cacheKey].ks = null;
      console.log('Invalid Keystore promise for', cacheKey);
      console.error(error);
    }
  }

  if (entry.ks instanceof ctor)
    return entry.ks;
  else
    return new ctor(entry.ks);
}

/**
 * Determine if the cache has been popuated with a given keystore.
 *
 * @param {string} name       name of the key (eg 'id')
 * @param {any}    contextId
 *
 * @returns false if the cache does not have an entry for this key,
 *          true if it does, 'pending' if it is has a promise that
 *          is likely to resolve to the ks.  If that promise doesn't
 *          resolve as expected, it will either reject or resolve
 *          to falsey, and the cache lookup will always do that until
 *          the cache is cleared for that key.
 */
exports.has = function ksCache$$has(name, contextId)
{
  const cacheKey = buildCacheKey(name, contextId);

  if (!storage.hasOwnProperty(cacheKey))
    return false;

  if (isThenable(storage[cacheKey].ks))
    return 'pending';

  return true;
}


/**
 * Set a keystore or Promise that resolves to a keystore in the cache. A subsequent get() operation for
 * this keystore will await (and replace) the promise.
 *
 * @param {object}    ctor            the constructor for this Keystore
 * @param {string}    name            name of the key (eg 'id')
 * @param {any}       contextId
 * @param {object}    ks              an instance of Keystore or a Promise which resolves to
 *                                    an instance of Keystore, or null
 */
exports.set = function ksCache$$set(ctor, name, contextId, ks)
{
  const cacheKey = buildCacheKey(name, contextId);

  assert(isThenable(ks) || ks instanceof Keystore || ks instanceof PrivateKey || ks === null)
  debugging() && !isThenable(ks) && console.debug(`wallet - ksCache set ${ctor.name} ${cacheKey} => ${ks && ks.address} (${ks && ks.label})`);
  debugging() &&  isThenable(ks) && console.debug(`wallet - ksCache set ${ctor.name} ${cacheKey} => <pending>`);
  assert(ctor === Keystore || ctor.prototype instanceof Keystore);

  if (ks instanceof PrivateKey)
    ks = new ctor(ks, '');

  return (storage[cacheKey] = {ctor, ks});
}

/**
 * Remove a keystore or Promise that resolve to a keystore from the cache.
 * @param {string}    name            name of the key (eg 'id')
 * @param {any}       contextId
 *
 * @returns false if the cache did not have an entry for that key, true if it did.
 */
exports.remove = function ksCache$$remove(name, contextId)
{
  const cacheKey = buildCacheKey(name, contextId);
  if (!storage.hasOwnProperty(cacheKey))
    return false;
  delete storage[cacheKey];
  return true;
}

exports.import = function ksCache$$import(s)
{
  const o = KVIN.parse(s);
  if (o.version !== '1.0.0')
    throw new Error('Cannot important wallet; incompatible version');

  Object.apply(storage, o.storage);
}

exports.export = function ksCache$$export()
{
  return KVIN.serialize({ version: '1.0.0.', storage });
}
